<script lang="ts" setup>
import { ArrowTopRightOnSquareIcon, StarIcon as StarOutlineIcon } from "@heroicons/vue/24/outline";
import { ServerStackIcon, StarIcon as StarSolidIcon } from "@heroicons/vue/24/solid";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { twMerge } from "tailwind-merge";
import TableRow from "./ui/table/TableRow.vue";
import TableCell from "./ui/table/TableCell.vue";
import ACLClickToGrantButton from "./ACLClickToGrantButton.vue";
import ACLStatusWithChildInfo from "./ACLStatusWithChildInfo.vue";
import GrantCredentialsDialog, { type ACLRequirements, type GrantCredentials } from "./GrantCredentialsDialog.vue";
import { DateTime } from "luxon";
import { useAuthStore } from "../pinia/auth.ts";
import Tooltip from "./ui/Tooltip.vue";
import { getEffectiveAclCardState } from "../shared/services/effectiveAclCardState";

const props = defineProps({
  mode: {
    type: String,
    required: true,
  },
  acl: {
    type: Object,
    required: true,
  },
  childAcl: {
    type: Object,
    required: false,
  },
  onGrantAcl: {
    type: Function,
    required: false,
  },
  onRevokeAcl: {
    type: Function,
    required: false,
  },
  onIncreaseAclUseCount: {
    type: Function,
    required: false,
  },
  onFavouriteAcl: {
    type: Function,
    required: false,
  },
  isFavourite: {
    type: Boolean,
    required: false,
    default: false,
  },
});

onMounted(async () => {
  if (props.mode !== "card" && props.mode !== "row") {
    throw new Error("invalid mode for ACL componenet");
  }
});

const { t } = useI18n();
const router = useRouter();

const authStore = useAuthStore();

const user = computed(() => authStore.user);
const userHasTotpEnabled = computed(() => user.value?.TotpEnabled && user.value?.UserVerified);

const cardState = computed(() =>
  getEffectiveAclCardState({
    acl: props.acl,
    childAcl: props.childAcl ?? null,
    userHasTotpEnabled: !!userHasTotpEnabled.value,
    hasDevices: (props.acl.PersistentIPs?.length ?? 0) > 0,
  }),
);
const url = computed(() => props.acl.URL);
const displayUrl = computed(() => getDisplayRoute(props.acl.URL));

const expiresAfter = ref(DateTime.fromISO(props.expiredAfter, { zone: "utc" }));
const expiresDiff = computed(() => (expiresAfter.value ? expiresAfter.value.diffNow("seconds").seconds : null));

// Grant credentials dialog state
const showCredentialsDialog = ref(false);
const pendingGrantEvent = ref<Event | null>(null);
const credentialsDialogLoading = ref(false);
const credentialsDialogError = ref<string | null>(null);
const credentialsDialogRateLimitSeconds = ref(0);

const aclRequirements = computed<ACLRequirements>(() => ({
  requireTicketNumber: props.acl.RequireTicketNumber ?? false,
  requireTOTP: props.acl.RequireTOTP ?? false,
  description: props.acl.Description ?? "",
}));

const needsCredentials = computed(() => aclRequirements.value.requireTicketNumber || aclRequirements.value.requireTOTP);

function handleGrantClick(e: Event) {
  if (needsCredentials.value) {
    pendingGrantEvent.value = e;
    credentialsDialogError.value = null;
    credentialsDialogLoading.value = false;
    showCredentialsDialog.value = true;
    return;
  }
  // Return the grant promise so the button can track in-flight state.
  return props.onGrantAcl?.(e);
}

async function handleCredentialsSubmit(credentials: GrantCredentials) {
  // Store credentials on the ACL object for the grant handler to use
  if (credentials.ticketNumber) {
    props.acl.TicketNumber = credentials.ticketNumber;
  }
  if (credentials.totpCode) {
    props.acl.TOTPCode = credentials.totpCode;
  }

  credentialsDialogError.value = null;
  credentialsDialogRateLimitSeconds.value = 0;
  credentialsDialogLoading.value = true;

  try {
    if (pendingGrantEvent.value) {
      await props.onGrantAcl?.(pendingGrantEvent.value);
      pendingGrantEvent.value = null;
    }
    // Success - close the dialog
    showCredentialsDialog.value = false;
    credentialsDialogLoading.value = false;
  } catch (error: any) {
    // Keep dialog open and show error
    credentialsDialogLoading.value = false;

    // Check for rate limiting (429 status)
    if (error?.response?.status === 429) {
      const retryAfter = parseInt(error.response.headers["retry-after"] || "30", 10);
      credentialsDialogRateLimitSeconds.value = retryAfter;
      credentialsDialogError.value = null;
      return;
    }

    // Server returns { Err: "err.grantTotpInvalid" } format - use it as i18n key
    const errKey = error?.response?.data?.Err;
    if (errKey && errKey.startsWith("err.")) {
      credentialsDialogError.value = t(errKey);
    } else {
      credentialsDialogError.value = error?.message || t("err.grantFailed");
    }
  }
}

function handleCredentialsClose() {
  showCredentialsDialog.value = false;
  pendingGrantEvent.value = null;
  credentialsDialogError.value = null;
  credentialsDialogLoading.value = false;
  credentialsDialogRateLimitSeconds.value = 0;
}

let interval;

onMounted(() => {
  interval = setInterval(async () => {
    if (expiresDiff.value !== null && expiresDiff.value < 0) {
      await authStore.fetchStatus({ force: true });
    }
  }, 1000);
});

onBeforeUnmount(() => {
  if (interval) {
    clearInterval(interval);
  }
});

function getDisplayRoute(url: string): string {
  try {
    const parsedUrl = new URL(url);
    const port = parsedUrl.port ? `:${parsedUrl.port}` : "";
    return `${parsedUrl.protocol}//${parsedUrl.hostname}${port}`;
  } catch {
    return url;
  }
}

function navigateToTotpSetup() {
  router.push({ name: "TwoFactorSetup" });
}
</script>

<template>
  <template v-if="mode === 'card'">
    <div
      :class="
        twMerge(
          'flex h-full min-h-[134px] flex-col justify-between space-y-4 rounded-md border bg-muted/30 px-4 py-4 dark:border-white/20',
          cardState.disabled && !cardState.disabledDueToTOTP
            ? 'pointer-events-none cursor-not-allowed opacity-50'
            : null,
          cardState.disabledDueToTOTP ? 'cursor-default' : null,
        )
      "
    >
      <div :class="['flex justify-between', cardState.disabledDueToTOTP ? 'opacity-50' : '']">
        <div class="flex flex-col overflow-hidden">
          <component
            :is="cardState.disabled || !Boolean(url) ? 'div' : 'a'"
            :aria-label="
              cardState.disabled || !Boolean(url)
                ? `ACL for ${acl.Description}`
                : `Open '${acl.Description}' ${url} in a new tab`
            "
            :class="{
              'pointer-events-none': cardState.disabled || !Boolean(url),
            }"
            v-bind="
              cardState.disabled || !Boolean(url)
                ? {}
                : {
                    href: url,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  }
            "
            @click="!cardState.disabled && Boolean(url) && onIncreaseAclUseCount?.()"
          >
            <div class="flex items-center space-x-1">
              <font-awesome-icon :icon="acl.Icon" fixed-width />

              <Tooltip :text="acl.Description" class="min-w-0">
                <h3 aria-hidden="true" class="mr-2 truncate font-medium">
                  {{ acl.Description }}
                </h3>
              </Tooltip>
            </div>

            <span v-if="Boolean(url)" class="inline-flex max-w-xs items-center truncate text-sm text-muted-foreground">
              <ArrowTopRightOnSquareIcon class="mr-1 h-3 w-3 flex-shrink-0" />
              <span class="truncate">{{ getDisplayRoute(url) }}</span>
            </span>
          </component>
        </div>

        <div>
          <div class="flex items-center space-x-4">
            <Tooltip
              v-if="acl.GrantTypeFixedIps"
              :focusable="acl.GrantTypeFixedIps"
              :aria-label="t('fields.acls.fixedIpsAria', { name: acl.Description })"
              :text="t('fields.acls.fixedIpsTooltip')"
            >
              <span class="flex items-center">
                <ServerStackIcon class="h-4 w-4" />
              </span>
            </Tooltip>

            <component
              :is="cardState.disabled ? 'div' : 'button'"
              v-if="isFavourite"
              v-bind="cardState.disabled ? {} : { 'aria-label': t('pages.home.sections.favourites.removeAriaLabel') }"
              @click="cardState.disabled ? undefined : onFavouriteAcl?.($event)"
            >
              <StarSolidIcon :class="['h-4 w-4', cardState.disabled ? 'cursor-not-allowed' : 'cursor-pointer']" />
            </component>
            <component
              :is="cardState.disabled ? 'div' : 'button'"
              v-bind="cardState.disabled ? {} : { 'aria-label': t('pages.home.sections.favourites.addAriaLabel') }"
              v-else
              @click="cardState.disabled ? undefined : onFavouriteAcl?.($event)"
            >
              <StarOutlineIcon :class="['h-4 w-4', cardState.disabled ? 'cursor-not-allowed' : 'cursor-pointer']" />
            </component>
          </div>
        </div>
      </div>
      <!-- Bottom row: the grant/revoke button is always anchored bottom-left; all
           status, info and blocked-reason content sits on the right. Nothing about
           the card state moves the button out of the left slot. -->
      <div class="flex min-h-8 items-end justify-between gap-3">
        <!-- LEFT: grant/revoke button only (the two states that render one). -->
        <div class="flex items-end">
          <ACLClickToGrantButton
            v-if="cardState.section === 'clickToGrant' || cardState.section === 'expiredRenew'"
            :key="acl.ACLID"
            :expired-after="acl.ExpiredAfter"
            :status="cardState.buttonStatus"
            :revoking="cardState.revoking"
            :awaiting-agent="cardState.awaitingAgent"
            :on-grant-acl="handleGrantClick"
            :on-revoke-acl="onRevokeAcl"
          />
        </div>

        <!-- RIGHT: status badge + state info / blocked reasons + the TOTP setup button. -->
        <div class="flex min-w-0 flex-col items-end gap-1 text-right">
          <template v-if="cardState.section === 'totpSetup'">
            <span class="text-sm font-medium text-muted-foreground">{{ t("fields.acls.totpRequired") }}</span>
            <button
              type="button"
              class="pointer-events-auto shrink-0 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              @click="navigateToTotpSetup"
            >
              {{ t("fields.acls.setupTotp") }}
            </button>
          </template>
          <template v-else-if="cardState.section === 'disabledByAdmin'">
            <span class="text-sm font-medium text-muted-foreground">{{ t("common.disabled") }}</span>
            <span class="text-xs text-muted-foreground/70">{{ t("fields.acls.consultYourAdmin") }}</span>
          </template>
          <template v-else-if="cardState.section === 'agentOffline'">
            <span class="text-sm font-medium text-muted-foreground">Agent offline</span>
            <span class="text-xs text-muted-foreground/70">{{ t("fields.acls.consultYourAdmin") }}</span>
          </template>
          <template v-else-if="cardState.section === 'greynoiseBlocked'">
            <span class="text-sm font-medium text-muted-foreground">{{ t("fields.acls.greynoiseBlockedTitle") }}</span>
            <span class="text-xs text-muted-foreground/70">{{ t("fields.acls.greynoiseBlockedSubtext") }}</span>
          </template>
          <template v-else-if="cardState.section === 'staticBlocked'">
            <span class="text-sm font-medium text-muted-foreground">{{ t("fields.acls.staticBlockedTitle") }}</span>
            <span class="text-xs text-muted-foreground/70">{{ t("fields.acls.staticBlockedSubtext") }}</span>
          </template>
          <template v-else-if="cardState.section === 'ipExcluded'">
            <span class="text-sm font-medium text-muted-foreground">{{ t("fields.acls.ipExcludedTitle") }}</span>
            <span class="text-xs text-muted-foreground/70">{{ t("fields.acls.ipExcludedSubtext") }}</span>
          </template>
          <template v-else-if="cardState.section === 'blockedGeneric'">
            <span class="text-sm font-medium text-muted-foreground">{{ t("fields.acls.blockedGenericTitle") }}</span>
            <span class="text-xs text-muted-foreground/70">{{ t("fields.acls.blockedGenericSubtext") }}</span>
          </template>
          <template v-else-if="cardState.section === 'expiredRenew'">
            <span class="text-sm font-medium text-muted-foreground">{{ t("fields.acls.expiredRenewTitle") }}</span>
            <span class="text-xs text-muted-foreground/70">{{ t("fields.acls.expiredRenewSubtext") }}</span>
          </template>
          <template v-else-if="cardState.section === 'pending'">
            <span class="text-sm font-medium text-muted-foreground">Pending</span>
          </template>

          <ACLStatusWithChildInfo
            v-if="cardState.showStatusBadge"
            :key="`${acl.ACLID}-${acl.Status}`"
            :child-info="cardState.childInfo"
            :click-to-grant="acl.ClickToGrant"
            :error="cardState.badgeError"
            :expired-after="acl.ExpiredAfter"
            :status="cardState.badgeStatus"
            :delegated-ips="acl.PersistentIPs"
            :grant-type-fixed-ips="acl.GrantTypeFixedIps"
            :current-address="user?.CurrentAddress"
            align="end"
          />
        </div>
      </div>
    </div>
  </template>
  <template v-if="mode === 'row'">
    <TableRow
      :class="{
        'cursor-not-allowed opacity-50': cardState.disabled && !cardState.disabledDueToTOTP,
        'pointer-events-none': cardState.disabled && !cardState.disabledDueToTOTP,
      }"
    >
      <TableCell>
        <div class="flex items-center space-x-4">
          <div>
            <StarSolidIcon
              v-if="isFavourite && !cardState.disabled"
              class="h-4 w-4 cursor-pointer"
              @click="!cardState.disabled && onFavouriteAcl?.($event)"
            />
            <StarOutlineIcon
              v-else-if="!cardState.disabled"
              class="h-4 w-4 cursor-pointer"
              @click="!cardState.disabled && onFavouriteAcl?.($event)"
            />
            <StarSolidIcon v-else-if="isFavourite && cardState.disabled" class="h-4 w-4 text-gray-400" />
            <StarOutlineIcon v-else class="h-4 w-4 text-gray-400" />
          </div>

          <Tooltip
            v-if="acl.GrantTypeFixedIps"
            :focusable="acl.GrantTypeFixedIps"
            :aria-label="t('fields.acls.fixedIpsAria', { name: acl.Description })"
            :text="t('fields.acls.fixedIpsTooltip')"
          >
            <span class="flex items-center">
              <ServerStackIcon class="h-4 w-4" />
            </span>
          </Tooltip>
        </div>
      </TableCell>

      <TableCell class="truncate" colspan="2">
        <template v-if="cardState.disabled">
          <div :class="cardState.disabledDueToTOTP ? 'text-gray-500' : 'text-gray-500'">
            <div :class="cardState.disabledDueToTOTP ? 'opacity-50' : ''">
              {{ acl.Description }}
            </div>
            <div
              v-if="Boolean(url)"
              :class="[
                'mt-1 text-sm text-muted-foreground md:ml-3 md:mt-0 md:inline',
                cardState.disabledDueToTOTP ? 'opacity-50' : '',
              ]"
            >
              <span class="hidden md:inline">&nbsp;&nbsp;&nbsp;</span>{{ displayUrl }}
            </div>
            <div class="mt-1 text-sm italic">
              <template v-if="cardState.section === 'totpSetup'">
                <span class="opacity-50">{{ t("fields.acls.totpRequired") }}</span>
                <span class="opacity-50"> - </span>
                <span class="opacity-50">{{ t("fields.acls.setupTotpFirst") }}</span>
                <button
                  type="button"
                  class="ml-2 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground opacity-100 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  @click="navigateToTotpSetup"
                >
                  {{ t("fields.acls.setupTotp") }}
                </button>
              </template>
              <template v-else-if="cardState.section === 'disabledByAdmin'">
                <span>{{ t("common.disabled") }}</span>
                <span> - </span>
                <span>{{ t("fields.acls.consultYourAdmin") }}</span>
              </template>
              <template v-else>
                <span>Agent offline</span>
                <span> - </span>
                <span>{{ t("fields.acls.consultYourAdmin") }}</span>
              </template>
            </div>
          </div>
        </template>
        <template v-else>
          <a v-if="Boolean(url)" :href="url" class="block md:inline" target="_blank">
            <div class="md:inline">{{ acl.Description }}</div>
            <div class="mt-1 text-sm text-muted-foreground md:ml-3 md:mt-0 md:inline">
              <span class="hidden md:inline">&nbsp;&nbsp;&nbsp;</span>{{ displayUrl }}
            </div>
          </a>
          <div v-else>
            <span>{{ acl.Description }}</span>
          </div>
        </template>
      </TableCell>

      <!-- Only show access controls if not disabled -->
      <template v-if="!cardState.disabled">
        <!-- Desktop: Status column -->
        <TableCell v-if="cardState.showStatusBadge" class="hidden py-2 text-center md:table-cell">
          <ACLStatusWithChildInfo
            :key="`${acl.ACLID}-${acl.Status}`"
            :child-info="cardState.childInfo"
            :click-to-grant="acl.ClickToGrant"
            :error="cardState.badgeError"
            :expired-after="acl.ExpiredAfter"
            :status="cardState.badgeStatus"
            :delegated-ips="acl.PersistentIPs"
            :grant-type-fixed-ips="acl.GrantTypeFixedIps"
            :current-address="user?.CurrentAddress"
            align="center"
          />
        </TableCell>
        <TableCell v-else class="hidden py-2 md:table-cell" />

        <!-- Desktop: Access column -->
        <TableCell
          v-if="cardState.section === 'clickToGrant' || cardState.section === 'expiredRenew'"
          class="hidden py-2 text-center md:table-cell"
        >
          <ACLClickToGrantButton
            :key="acl.ACLID"
            :expired-after="acl.ExpiredAfter"
            :revoking="cardState.revoking"
            :awaiting-agent="cardState.awaitingAgent"
            :on-grant-acl="handleGrantClick"
            :on-revoke-acl="onRevokeAcl"
            :status="cardState.buttonStatus"
          />
        </TableCell>
        <TableCell v-else class="hidden py-2 md:table-cell" />

        <!-- Mobile: Combined Status + Access -->
        <TableCell class="py-2 text-center md:hidden" colspan="2">
          <div class="flex flex-col items-center space-y-2">
            <ACLStatusWithChildInfo
              v-if="cardState.showStatusBadge"
              :key="`${acl.ACLID}-${acl.Status}`"
              :child-info="cardState.childInfo"
              :click-to-grant="acl.ClickToGrant"
              :error="cardState.badgeError"
              :expired-after="acl.ExpiredAfter"
              :status="cardState.badgeStatus"
              :delegated-ips="acl.PersistentIPs"
              :grant-type-fixed-ips="acl.GrantTypeFixedIps"
              :current-address="user?.CurrentAddress"
              align="center"
            />
            <ACLClickToGrantButton
              v-if="cardState.section === 'clickToGrant' || cardState.section === 'expiredRenew'"
              :key="acl.ACLID"
              :expired-after="acl.ExpiredAfter"
              :revoking="cardState.revoking"
              :awaiting-agent="cardState.awaitingAgent"
              :on-grant-acl="handleGrantClick"
              :on-revoke-acl="onRevokeAcl"
              :status="cardState.buttonStatus"
            />
          </div>
        </TableCell>
      </template>

      <!-- Show disabled message for access columns when disabled -->
      <template v-else>
        <TableCell class="hidden py-2 md:table-cell" />
        <TableCell class="hidden py-2 text-center text-gray-400 md:table-cell">
          <span class="italic">Access restricted</span>
        </TableCell>
        <TableCell class="py-2 text-center text-gray-400 md:hidden" colspan="2">
          <span class="italic">Access restricted</span>
        </TableCell>
      </template>
    </TableRow>
  </template>

  <!-- Grant Credentials Dialog -->
  <GrantCredentialsDialog
    :show="showCredentialsDialog"
    :acl="aclRequirements"
    :error="credentialsDialogError"
    :is-loading="credentialsDialogLoading"
    :rate-limit-seconds="credentialsDialogRateLimitSeconds"
    @close="handleCredentialsClose"
    @submit="handleCredentialsSubmit"
    @rate-limit-expired="credentialsDialogRateLimitSeconds = 0"
  />
</template>
